import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, FileText, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#features', label: 'Features' },
    { href: '#how-it-works', label: 'How It Works' },
    { href: '#pricing', label: 'Pricing' },
    { href: '#docs', label: 'Documentation' },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
        ? 'bg-white/80 backdrop-blur-lg shadow-sm border-b border-gray-100'
        : 'bg-transparent'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2 group" data-testid="nav-logo">
            <div className="w-9 h-9 bg-[#635BFF] rounded-xl flex items-center justify-center transition-transform duration-200 group-hover:scale-105">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-bold text-[#0A2540]">
              SmartOCR
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-[#425466] font-medium hover:text-[#635BFF] transition-colors duration-200"
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <Button
              variant="ghost"
              className="text-[#425466] font-medium hover:text-[#635BFF] hover:bg-transparent"
              data-testid="nav-signin-btn"
              onClick={() => window.location.href = 'https://app.smartocr.my.id/login'}
            >
              Sign In
            </Button>
            <Button
              className="bg-[#635BFF] hover:bg-[#5851E0] text-white rounded-full px-6 font-semibold transition-all duration-200 active:scale-95"
              data-testid="nav-start-btn"
            >
              <Zap className="w-4 h-4 mr-2" />
              Start Free
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-[#0A2540]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden bg-white border-t border-gray-100"
            >
              <div className="py-4 space-y-3">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="block px-4 py-2 text-[#425466] font-medium hover:text-[#635BFF] hover:bg-[#F7F8FC] rounded-lg transition-colors duration-200"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                ))}
                <div className="px-4 pt-4 space-y-3 border-t border-gray-100">
                  <Button
                    variant="outline"
                    className="w-full justify-center rounded-full"
                    data-testid="mobile-signin-btn"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      window.location.href = 'https://app.smarocr.my.id/login';
                    }}
                  >
                    Sign In
                  </Button>
                  <Button
                    className="w-full justify-center bg-[#635BFF] hover:bg-[#5851E0] text-white rounded-full font-semibold"
                    data-testid="mobile-start-btn"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Start Free
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};
